import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'react-feather';

const selectedIds = (value) => String(value || '').split(',').filter(Boolean);

const AgentMultiSelect = ({ agents, value, onChange }) => {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState(() => selectedIds(value));

  useEffect(() => setDraft(selectedIds(value)), [value]);
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeWithEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        rootRef.current?.querySelector('.ep-agent-trigger')?.focus();
      }
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeWithEscape);
    searchRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeWithEscape);
    };
  }, [open]);

  const visibleAgents = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return agents;
    return agents.filter((agent) => [
      agent.fullName || agent.agentName,
      agent.firstName,
      agent.lastName,
      agent.employeeNumber,
      agent.agentEmail,
    ].some((field) => String(field || '').toLowerCase().includes(needle)));
  }, [agents, search]);

  const summary = !selectedIds(value).length
    ? 'Tous les agents'
    : `${selectedIds(value).length} agent${selectedIds(value).length > 1 ? 's' : ''} sélectionné${selectedIds(value).length > 1 ? 's' : ''}`;
  const toggle = (employeeId) => setDraft((current) =>
    current.includes(employeeId)
      ? current.filter((id) => id !== employeeId)
      : [...current, employeeId]
  );
  const apply = () => {
    onChange(draft.join(','));
    setOpen(false);
  };

  return <div className="ep-agent-select" ref={rootRef}>
    <span className="ep-agent-label">Agents</span>
    <button
      type="button"
      className="ep-agent-trigger"
      aria-haspopup="listbox"
      aria-expanded={open}
      onClick={() => setOpen((current) => !current)}
    >
      <span><b>Agents</b><small>{summary}</small></span>
      <ChevronDown aria-hidden="true" />
    </button>
    {open && <div className="ep-agent-menu">
      <div className="ep-agent-menu-head">
        <strong>Sélectionner des employés</strong>
        <button type="button" aria-label="Fermer la sélection des agents" onClick={() => setOpen(false)}><X /></button>
      </div>
      <label className="ep-agent-search"><Search aria-hidden="true" />
        <input
          ref={searchRef}
          aria-label="Rechercher dans les agents"
          placeholder="Prénom, nom ou numéro"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      <div className="ep-agent-tools">
        <button type="button" onClick={() => setDraft(agents.map((agent) => agent.employeeId || agent.agentKey))}>Tout sélectionner</button>
        <button type="button" onClick={() => setDraft([])}>Effacer</button>
      </div>
      <label className="ep-agent-option ep-agent-all">
        <input type="checkbox" checked={!draft.length} onChange={() => setDraft([])} />
        <span><b>Tous les agents</b><small>Aucun filtre individuel</small></span>
        {!draft.length && <Check aria-hidden="true" />}
      </label>
      <div className="ep-agent-options" role="listbox" aria-label="Agents" aria-multiselectable="true">
        {visibleAgents.map((agent) => {
          const employeeId = agent.employeeId || agent.agentKey;
          const checked = draft.includes(employeeId);
          return <label className="ep-agent-option" key={employeeId} role="option" aria-selected={checked}>
            <input type="checkbox" checked={checked} onChange={() => toggle(employeeId)} />
            <span>
              <b>{agent.fullName || agent.agentName}</b>
              <small>Employé #{agent.employeeNumber || employeeId}{agent.terminated ? ' · Terminé' : ''}</small>
            </span>
            {checked && <Check aria-hidden="true" />}
          </label>;
        })}
        {!visibleAgents.length && <p className="ep-agent-no-result">Aucun employé trouvé.</p>}
      </div>
      <button type="button" className="ep-primary ep-agent-apply" onClick={apply}>Appliquer</button>
    </div>}
  </div>;
};

export default AgentMultiSelect;
